// ============================================================================
// PRICE FORMATTING
// ============================================================================

export function formatPrice(
  price: number,
  currency: string = "USD",
  locale: string = "en-US",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Canonical money formatter for documents/invoices/portal/PDF — "12 345 Kč" / "1 234 €" with
 * cs-CZ thin-space grouping and a currency suffix (CZK→Kč, EUR→€, else the raw code). The app
 * stores money as WHOLE units of its currency (not minor units), so no division. Single source
 * of truth so a contract PDF and its invoice can never disagree on formatting. NOTE: qrPayment's
 * SPAYD/EPC amount (toFixed(2)) is a machine payload, NOT display — keep it separate.
 */
export function formatMoney(
  amount: number,
  currency: string = "CZK",
  locale: string = "cs-CZ",
): string {
  const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount || 0);
  const cur = (currency || "CZK").toUpperCase();
  const suffix = cur === "CZK" ? "Kč" : cur === "EUR" ? "€" : cur;
  return `${n} ${suffix}`;
}

export function formatPriceCompact(price: number): string {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)} mil.`;
  }
  if (price >= 1000) {
    return `${Math.round(price / 1000)} tis.`;
  }
  return price.toString();
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = "en-US",
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    ...options,
  });
}

export function formatDateRange(
  startDate: Date | string,
  endDate: Date | string,
  locale: string = "cs-CZ",
): string {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const startStr = start.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
  });
  const endStr = end.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

  return `${startStr} - ${endStr}`;
}

export function formatRelativeDate(date: Date | string, locale: string = "cs"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "dnes";
  if (diffDays === 1) return "zítra";
  if (diffDays === -1) return "včera";
  if (diffDays > 0 && diffDays <= 7)
    return `za ${diffDays} ${pluralize(diffDays, "den", "dny", "dní")}`;
  if (diffDays < 0 && diffDays >= -7)
    return `před ${Math.abs(diffDays)} ${pluralize(Math.abs(diffDays), "dnem", "dny", "dny")}`;

  return formatDate(d, undefined, locale);
}

export function daysUntil(date: Date | string): number {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

// ============================================================================
// STRING UTILITIES
// ============================================================================

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Remove consecutive hyphens
}

/**
 * Derive a fallback `alt` text from a filename when AI alt-suggest fails.
 * Strips the extension, replaces underscores/dashes/dots/brackets with spaces,
 * and collapses runs of whitespace — so screen readers don't read out
 * `vietnam_trip.v2.heic` as "vietnam underscore trip dot v two dot heic".
 * Shared between the MediaPicker (admin) and the upload route (api).
 */
export function altFromFilename(filename: string): string {
  return (
    filename
      .replace(/\.[^.]+$/, "")
      .replace(/[._\-()[\]{}]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "image"
  );
}

export function truncate(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).replace(/\s+\S*$/, "") + "...";
}

export function pluralize(count: number, singular: string, few: string, many: string): string {
  if (count === 1) return singular;
  if (count >= 2 && count <= 4) return few;
  return many;
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ============================================================================
// TRIP UTILITIES
// ============================================================================

export function formatDuration(days: number): string {
  if (days === 1) return "1 den";
  if (days >= 2 && days <= 4) return `${days} dny`;
  return `${days} dní`;
}

/**
 * Format trip duration based on tripKind. Locale "cs" returns Czech (used in PDFs/emails);
 * "en" returns English (used on the public web).
 */
export function formatTripDuration(
  trip: {
    tripKind?: "tour" | "experience" | null;
    durationDays?: number | null;
    durationHours?: number | null;
  },
  locale: "cs" | "en" = "en",
): string {
  const isExperience = trip.tripKind === "experience";
  if (isExperience) {
    const h = trip.durationHours || 0;
    return locale === "cs" ? `${h} hodin` : `${h} hours`;
  }
  const d = trip.durationDays || 0;
  return locale === "cs" ? `${d} dni` : `${d} days`;
}

/**
 * Convert allowed weekdays (1..6,0) to disabled list for Calendar component.
 * If allowed is null/empty (= all days), returns empty disabled list.
 */
export function invertWeekdays(allowed: number[] | null | undefined): number[] {
  if (!allowed || allowed.length === 0) return [];
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => !allowed.includes(d));
}

/** Compute earliest allowed booking date given lead time in hours from now. */
export function leadTimeMinDate(leadTimeHours: number): Date {
  return new Date(Date.now() + leadTimeHours * 3600_000);
}

/**
 * The BOOKING axis (Pre-launch Area 3): does this trip book on an open calendar (`on_request`)
 * or from fixed departure dates? Reads `trips.fulfillmentMode`; falls back to
 * `tripKind === "experience"` for rows not yet backfilled. Use this for EVERY booking-axis
 * branch (calendar vs dropdown, pricing fallback, seat holds, inquiry validation) — never read
 * `tripKind` directly for booking, since multi-day TOURS can be on-request too.
 */
export function isOnRequestBooking(trip: {
  fulfillmentMode?: "fixed_departures" | "on_request" | null;
  tripKind?: "tour" | "experience" | null;
}): boolean {
  if (trip.fulfillmentMode) return trip.fulfillmentMode === "on_request";
  return trip.tripKind === "experience";
}

export function formatGroupSize(min: number, max: number): string {
  if (min === max) return `${min} osob`;
  return `${min}-${max} osob`;
}

export function calculateDiscount(originalPrice: number, currentPrice: number): number {
  if (!originalPrice || originalPrice <= currentPrice) return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

export function getAvailableSpots(capacity: number, booked: number): number {
  return Math.max(0, capacity - booked);
}

export function isFewSpots(capacity: number, booked: number): boolean {
  const available = getAvailableSpots(capacity, booked);
  return available > 0 && available <= 3;
}

/**
 * Derive a departure's occupancy level (Vlna 2 / Block 4.1, R1) from booked vs capacity —
 * the DERIVED axis that replaces the old stored `full`/`few_spots`/`open` status values.
 * Shared by web, admin and API so the threshold can never drift. `waitlist > 0` means the
 * departure is already over capacity, so it is full regardless of the booked/capacity ratio.
 */
export function occupancyLevel(
  booked: number,
  capacity: number,
  waitlist = 0,
): "open" | "few_spots" | "full" {
  if (waitlist > 0) return "full";
  const available = getAvailableSpots(capacity, booked);
  if (available <= 0) return "full";
  if (available <= 3) return "few_spots";
  return "open";
}

// ============================================================================
// ITINERARY UTILITIES
// ============================================================================

/**
 * Normalize meals from legacy object format {breakfast: true, dinner: true}
 * or new array format ["breakfast", "dinner"] to a consistent string[].
 */
export function normalizeMeals(meals?: string[] | Record<string, boolean | undefined>): string[] {
  if (!meals) return [];
  if (Array.isArray(meals)) return meals;
  return Object.entries(meals)
    .filter(([, v]) => v)
    .map(([k]) => k);
}

// ============================================================================
// ORDER UTILITIES
// ============================================================================

export function generateOrderNumber(prefix: string = "BMT"): string {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${year}-${random}`;
}

export function generateVoucherCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Exclude confusing characters
  let code = "BMT-GIFT-";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// ============================================================================
// BLOG CONSTANTS
// ============================================================================

export const BLOG_CATEGORY_LABELS: Record<string, string> = {
  "travel-tips": "Travel Tips",
  culture: "Culture",
  food: "Food & Cuisine",
  nature: "Nature",
  guides: "Guides",
  news: "News",
};

// ============================================================================
// URL UTILITIES
// ============================================================================

export function getYoutubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function isYouTubeShort(url: string): boolean {
  return url.includes("/shorts/");
}

export function getYoutubeThumbnail(
  url: string,
  quality: "default" | "medium" | "high" | "maxres" = "high",
): string | null {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  const qualityMap = {
    default: "default",
    medium: "mqdefault",
    high: "hqdefault",
    maxres: "maxresdefault",
  };

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function parseQueryString(query: string): Record<string, string> {
  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};

  for (const [key, value] of params) {
    result[key] = value;
  }

  return result;
}

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

export function groupBy<T, K extends string | number>(
  array: T[],
  keyFn: (item: T) => K,
): Record<K, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}

export function uniqueBy<T>(array: T[], keyFn: (item: T) => unknown): T[] {
  const seen = new Set();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Split an array into chunks of `size`. The default 80 keeps a chunked `inArray` (one bound
 * parameter per element) safely under D1's 100-bound-parameters-per-query limit with headroom
 * for a few extra WHERE params. Multi-column INSERTs bind size×columns parameters — pass an
 * explicitly smaller size there (e.g. 20 rows × 4 columns = 80).
 */
export function chunk<T>(array: T[], size = 80): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size));
  return out;
}

export function sortBy<T>(
  array: T[],
  keyFn: (item: T) => number | string,
  order: "asc" | "desc" = "asc",
): T[] {
  const sorted = [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return order === "desc" ? sorted.reverse() : sorted;
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-()]/g, "");
  return /^\+?[0-9]{9,15}$/.test(cleaned);
}

export function sanitizePhone(phone: string): string {
  return phone.replace(/[\s\-()]/g, "");
}

/**
 * Normalize a phone number for identity matching (Block 5.5 — call pairing): strips
 * spaces, dashes, dots and parentheses, converts an international `00` prefix to `+`.
 * Returns "" for anything that isn't a plausible phone number (non-digit garbage or
 * fewer than 6 digits) — too short to identify anyone, so callers never match on it.
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return "";
  let p = phone.replace(/[\s\-().]/g, "");
  if (p.startsWith("00")) p = `+${p.slice(2)}`;
  if (!/^\+?\d+$/.test(p)) return "";
  return p.replace(/\D/g, "").length >= 6 ? p : "";
}

/**
 * Conservative phone equality for pairing: exact match after normalization, or a
 * last-9-digits match when BOTH sides have at least 9 digits (handles a missing
 * country code: 777123456 ≈ +420777123456). Short numbers never suffix-match —
 * a false pair attaches a transcript to the wrong person, so when in doubt: no match.
 */
export function phonesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const da = na.replace(/\D/g, "");
  const db = nb.replace(/\D/g, "");
  return da.length >= 9 && db.length >= 9 && da.slice(-9) === db.slice(-9);
}

// ============================================================================
// SAFE PARSING UTILITIES
// ============================================================================

/**
 * Safely parse an integer from a string value.
 * Returns the fallback if the value is undefined, null, empty, or NaN.
 */
export function safeParseInt(value: string | undefined | null, fallback: number): number {
  if (value === undefined || value === null || value === "") return fallback;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Safely parse JSON from a string value.
 * Returns the fallback if parsing fails.
 */
export function safeJsonParse<T>(input: string | undefined | null, fallback: T): T {
  if (input === undefined || input === null || input === "") return fallback;
  try {
    return JSON.parse(input) as T;
  } catch {
    return fallback;
  }
}

// ============================================================================
// ID GENERATION
// ============================================================================

export function generateId(prefix: string = ""): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}_${timestamp}${random}` : `${timestamp}${random}`;
}
